import {
  Home,
  ShoppingBag,
  Truck,
  Route as RouteIcon,
  Plug,
  BarChart3,
  BookOpen,
  Boxes,
  Tag,
  Bell,
  Users,
  Info,
  type LucideIcon,
} from 'lucide-react';

/**
 * Permission requirements for each top-level nav item, mirroring portal's
 * Sidebar permission map. A `null` requirement means the item is always
 * visible to authenticated users.
 * - `requires`: a single { resource, action }, or an array of them with
 *   ANY-OF (OR) semantics — checked via usePermission(). Use the array form
 *   to mirror legacy OR gates (e.g. Account Info = `merchant:get || vendor:search`).
 * - `restrictTo`: optional list of entity kinds that may see the item; ALL
 *   must match (e.g. super-admin only).
 */

export type EntityRestriction =
  | 'superAdmin'
  | 'merchantParent'
  | 'merchant'
  | 'multiMerchant'
  | 'vendorOrSuperAdmin';

export interface PermissionRequirement {
  resource: string;
  action: string;
}

export interface AppNavItem {
  path: string;
  /** i18n key under navigation:items.* */
  labelKey: string;
  icon: LucideIcon;
  /** A single permission, or an array satisfied when the user holds ANY of them. */
  requires: PermissionRequirement | PermissionRequirement[] | null;
  restrictTo?: EntityRestriction[];
  /**
   * Feature flag that must also evaluate true for the item to render —
   * checked via useFlagChecker(). Keep the key and default identical to the
   * target surface's route guard, so the nav never shows a link whose route
   * immediately redirects away when the flag is off.
   */
  requiresFlag?: { flag: string; defaultValue: boolean };
  /**
   * Extra route prefixes that should also light up this item, for surfaces
   * that conceptually belong to the nav entry but mount under a different
   * top-level path. Matched with the same exact-or-segment-boundary rule as
   * `path` (see Sidebar.isActive). Account Info uses this because the
   * merchant/vendor detail pages were re-rooted to `/admin/merchants/*` and
   * `/admin/vendors/*` in the new UI (legacy nested them under
   * `/admin/account-info/*`, so legacy's third-segment active check kept the
   * item highlighted).
   */
  activeWhenUnder?: string[];
  /**
   * Overrides the auto-derived test-id suffix (normally the kebab-cased path).
   * The structured Playwright suite targets the legacy id
   * `sidebar-link-admin-generalinfo` for General Info, but its path kebabs to
   * `admin-general-info`; pin the legacy suffix here so the derived id matches.
   */
  testIdSuffix?: string;
}

export interface AppNavSection {
  /** i18n key under navigation:sections.* — omitted for the unlabeled first section. */
  headingKey?: string;
  items: AppNavItem[];
}

export const appNavSections: AppNavSection[] = [
  {
    items: [
      {
        path: '/home',
        labelKey: 'home',
        icon: Home,
        requires: { resource: 'insight', action: 'get' },
      },
      {
        path: '/orders',
        labelKey: 'orders',
        icon: ShoppingBag,
        requires: { resource: 'order', action: 'search' },
      },
      {
        path: '/shipments',
        labelKey: 'shipments',
        icon: Truck,
        requires: [
          { resource: 'vendor', action: 'search' },
          { resource: 'vendor', action: 'create' },
        ],
        restrictTo: ['vendorOrSuperAdmin'],
        // Same flag + default as the ShipmentsRoutes guard, which redirects to
        // /home when the vendor portal is off — the link must hide with it,
        // as the legacy sidebar did.
        requiresFlag: { flag: 'omVendorPortalEnabled', defaultValue: true },
      },
    ],
  },
  {
    headingKey: 'admin',
    items: [
      {
        // Visible to anyone who can manage merchants OR vendors. Legacy gated on
        // `merchant:get || vendor:search`, but any vendor (incl. create-only) must
        // reach their own vendor record (facilities, FTP, VAS) from the nav — the
        // same population that sees Shipments/Products/Inventory, which gate on
        // `vendor:search || vendor:create`. So mirror that vendor half here; the
        // route is ungated and AccountInfoView renders only the tab(s) the user's
        // permissions allow.
        path: '/admin/account-info',
        labelKey: 'accountInfo',
        icon: Info,
        requires: [
          { resource: 'merchant', action: 'get' },
          { resource: 'vendor', action: 'search' },
          { resource: 'vendor', action: 'create' },
        ],
        // The merchant/vendor detail pages (Details, Settings, Templates, FTP,
        // Vendor Facilities, …) live at /admin/merchants/* and /admin/vendors/*
        // but are reached by drilling in from Account Info — keep this item
        // highlighted on those pages. Legacy nested them under
        // /admin/account-info/*, so its active check stayed lit.
        activeWhenUnder: ['/admin/merchants', '/admin/vendors'],
      },
      {
        path: '/admin/products',
        labelKey: 'products',
        icon: Tag,
        requires: [
          { resource: 'vendor', action: 'search' },
          { resource: 'vendor', action: 'create' },
        ],
        restrictTo: ['vendorOrSuperAdmin'],
        // Same flag + default as the ProductsRoutes guard, which redirects to
        // /home when the vendor portal is off — the link must hide with it,
        // as the legacy sidebar did.
        requiresFlag: { flag: 'omVendorPortalEnabled', defaultValue: true },
      },
      {
        path: '/admin/routing',
        labelKey: 'routing',
        icon: RouteIcon,
        requires: { resource: 'routing', action: 'strategies_get' },
      },
      {
        path: '/admin/plugins',
        labelKey: 'plugins',
        icon: Plug,
        requires: [
          { resource: 'integration', action: 'plugins_get' },
          { resource: 'integration', action: 'plugins_create' }
        ]
      },
      {
        path: '/admin/exports',
        labelKey: 'exports',
        icon: BarChart3,
        requires: { resource: 'insight', action: 'report_get' },
      },
      {
        path: '/admin/catalogs',
        labelKey: 'catalogs',
        icon: BookOpen,
        requires: { resource: 'product', action: 'merchant_variants_view' },
      },
      {
        path: '/admin/inventory',
        labelKey: 'inventory',
        icon: Boxes,
        requires: [
          { resource: 'vendor', action: 'search' },
          { resource: 'vendor', action: 'create' },
        ],
      },
      {
        path: '/admin/sku-mappings',
        labelKey: 'skuMappings',
        icon: Tag,
        requires: null,
        restrictTo: ['superAdmin'],
      },
    ],
  },
  {
    headingKey: 'settings',
    items: [
      {
        path: '/admin/general-info',
        // Suite wants `sidebar-link-admin-generalinfo` (no hyphen); the path
        // kebabs to `admin-general-info`, so pin the legacy suffix.
        testIdSuffix: 'admin-generalinfo',
        labelKey: 'generalInfo',
        icon: Info,
        requires: { resource: 'merchant', action: 'get' },
        restrictTo: ['superAdmin', 'merchantParent', 'merchant'],
      },
      {
        path: '/admin/users',
        labelKey: 'usersAndRoles',
        icon: Users,
        requires: { resource: 'users', action: 'search' },
      },
      {
        path: '/admin/webhooks',
        labelKey: 'webhooks',
        icon: Bell,
        requires: { resource: 'notification', action: 'get' },
      },
    ],
  },
];
